import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import {
  cancelRunCenterRun,
  getRunCenterQueryKey,
  retryRunCenterRun,
  RUN_CENTER_OVERVIEW_LIMIT,
  type RunCenterMutationResult,
} from '../api/run-center.client.js';

export interface UseRunCenterMutationsReturn {
  cancel: ReturnType<typeof useMutation<RunCenterMutationResult, Error, string>>;
  retry: ReturnType<typeof useMutation<RunCenterMutationResult, Error, string>>;
}

export function useRunCenterMutations(): UseRunCenterMutationsReturn {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidateOverview = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: getRunCenterQueryKey(workspaceId, { limit: RUN_CENTER_OVERVIEW_LIMIT }),
    });
  };

  const cancel = useMutation<RunCenterMutationResult, Error, string>({
    mutationFn: (id: string) => cancelRunCenterRun(id),
    onSuccess: () => {
      void invalidateOverview();
    },
    onError: () => {
      message.error(t('crm.run_center.error.cancel_failed'));
    },
  });

  const retry = useMutation<RunCenterMutationResult, Error, string>({
    mutationFn: (id: string) => retryRunCenterRun(id),
    onSuccess: () => {
      void invalidateOverview();
    },
    onError: () => {
      message.error(t('crm.run_center.error.retry_failed'));
    },
  });

  return { cancel, retry };
}
